import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithGoogle, signInWithPartnerToken } from "../firebase";
import { partnerLogin } from "../api/partnerAuth";
import { SIGNOUT_REASON_KEY } from "../api/client";
import { errorBannerCls, inputCls, labelCls, buttonPrimaryCls } from "../styles/ui";
import logo from "../assets/logo.png";

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Why the user landed back here, when they were ejected rather than having
  // signed out themselves — set by apiClient on an `account_archived` 403.
  const [signedOutReason, setSignedOutReason] = useState(null);

  useEffect(() => {
    try {
      const reason = sessionStorage.getItem(SIGNOUT_REASON_KEY);
      if (reason) {
        setSignedOutReason(reason);
        // Read once: it explains this ejection, not every later visit.
        sessionStorage.removeItem(SIGNOUT_REASON_KEY);
      }
    } catch {
      /* sessionStorage throws in Safari private mode */
    }
  }, []);

  // Partner companies sign in with a shared password plus their own name.
  const [partnerMode, setPartnerMode] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [techName, setTechName] = useState("");

  async function handlePartnerSignIn(e) {
    e.preventDefault();
    if (!email.trim() || !password || !techName.trim()) {
      setError("Enter the email, password and your name.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await partnerLogin({ email: email.trim(), password, techName: techName.trim() });
      await signInWithPartnerToken(token);
      navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSignIn() {
    setLoading(true);
    setError(null);
    try {
      const user = await signInWithGoogle();
      if (user) navigate("/dashboard");
    } catch (err) {
      setError("Sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Brand panel — visible at md (1024px) and up */}
      <div className="hidden md:flex flex-col items-center justify-center w-2/5 max-w-sm bg-brand-600 px-12 py-16 shrink-0">
        {/* Logo on a white pill so it reads on the orange background */}
        <div className="bg-white/15 rounded-2xl px-5 py-3 mb-8">
          <img
            src={logo}
            alt="STINTE"
            className="h-10 w-auto"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
          />
        </div>
        <h1 className="text-3xl font-bold text-white tracking-tight">STINTE</h1>
        <p className="text-brand-100 text-sm mt-2 text-center">Field Operations Portal</p>

        {/* Subtle decorative rule */}
        <div className="mt-10 w-10 h-0.5 bg-brand-400 rounded-full" />

        <p className="mt-8 text-brand-200 text-xs text-center leading-relaxed max-w-[180px]">
          Scheduling · Purchase Orders · Field Docs
        </p>
      </div>

      {/* Sign-in panel */}
      <div className="flex-1 flex flex-col items-center justify-center bg-surface-muted px-6 py-12">

        {/* Mobile-only logo — shown below md */}
        <div className="md:hidden flex flex-col items-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-brand-600 flex items-center justify-center mb-4 shadow-md">
            <img
              src={logo}
              alt="STINTE"
              className="h-8 w-auto"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.parentElement.innerHTML = '<span class="text-white text-xl font-bold">S</span>';
              }}
            />
          </div>
          <h1 className="text-2xl font-bold text-ink">STINTE</h1>
          <p className="text-ink-muted text-sm mt-1">Field Operations Portal</p>
        </div>

        <div className="w-full max-w-sm">
          <h2 className="text-xl font-semibold text-ink mb-1">Sign in</h2>
          <p className="text-ink-muted text-sm mb-8">Use your company Google account to continue.</p>

          {signedOutReason && (
            <p className="mb-5 text-sm rounded-lg border border-warning-300 bg-warning-50 text-warning-800 px-3 py-2">
              {signedOutReason}
            </p>
          )}

          {error && <p className={`${errorBannerCls} mb-5`}>{error}</p>}

          {partnerMode ? (
            <form onSubmit={handlePartnerSignIn} className="space-y-4">
              <div>
                <label className={labelCls} htmlFor="partner-email">Email</label>
                <input
                  id="partner-email"
                  type="email"
                  autoComplete="username"
                  className={inputCls}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="partner-password">Password</label>
                <input
                  id="partner-password"
                  type="password"
                  autoComplete="current-password"
                  className={inputCls}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="partner-name">Your name</label>
                <input
                  id="partner-name"
                  autoComplete="name"
                  className={inputCls}
                  value={techName}
                  onChange={(e) => setTechName(e.target.value)}
                  placeholder="First and last name"
                />
              </div>
              <button type="submit" disabled={loading} className={`${buttonPrimaryCls} w-full`}>
                {loading ? "Signing in…" : "Sign in"}
              </button>
              <button
                type="button"
                onClick={() => { setPartnerMode(false); setError(null); }}
                className="w-full text-sm text-brand-700 hover:text-brand-600"
              >
                Back to Google sign-in
              </button>
            </form>
          ) : (
          <>
          <button
            onClick={handleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-surface hover:bg-zinc-50 active:bg-zinc-100 border border-zinc-300 text-ink font-medium text-sm py-3 rounded-lg shadow-sm transition-colors disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            {loading ? (
              <span className="text-ink-muted">Signing in…</span>
            ) : (
              <>
                <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => { setPartnerMode(true); setError(null); }}
            className="w-full mt-4 text-sm text-brand-700 hover:text-brand-600"
          >
            Partner company? Sign in here
          </button>
          </>
          )}

          <p className="text-center text-xs text-ink-muted mt-8">
            Access restricted to authorized STINTE personnel.
          </p>
        </div>
      </div>
    </div>
  );
}
